import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { observer } from 'mobx-react-lite'

// evertel
import {
    PageBody, Row, Col, Badge, Text,
    Breadcrumb, MenuItem, useUI
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { CurrentUserController } from '@evertel/blue-user'
import { DepartmentController } from '@evertel/department'
import {
    CloneSchemaController,
    DepartmentDocumentSchemasController,
    DepartmentSchemaCategoriesController,
    DocumentRecentTemplatesController
} from '@evertel/document'
import { APIDataDocumentSchema, APIDataDocumentSchemaCategory } from '@evertel/types'
import { DocumentTemplateCard } from '../elements'
import { CategoryList, BrowseCategory, categoryTabId } from './CategoryList'
import { TemplateGrid, TemplateGridItemProps } from './TemplateGrid'

const templatePanelId = 'my-templates-panel'

const MyTemplates: React.FC = observer(() => {

    const navigate = useNavigate()
    const { addToast, isConfirmed } = useUI()

    const sessionService = useService(SessionService, [])
    const currentUserController = useService(CurrentUserController, [])
    const departmentController = useService(DepartmentController, [])
    const departmentSchemaCategoriesController = useService(DepartmentSchemaCategoriesController, [])
    const departmentDocumentSchemasController = useService(DepartmentDocumentSchemasController, [])
    const documentRecentTemplatesController = useService(DocumentRecentTemplatesController, [])
    const cloneController = useService(CloneSchemaController, [])

    // selection and filter live in the URL so browser back (e.g. from the create page)
    // lands on the same category the user left from
    const [searchParams, setSearchParams] = useSearchParams()

    const updateParams = (updates: Record<string, string | null>) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            Object.entries(updates).forEach(([key, value]) => {
                if (value) next.set(key, value)
                else next.delete(key)
            })
            return next
        }, { replace: true })
    }

    const onSelectKey = (key: string) => updateParams({ category: key })

    // the search box is driven by local state, not the URL: setSearchParams is an async
    // navigation, so a controlled input bound to it re-renders with stale text between
    // keystrokes and drops characters. The URL is seeded once on mount and then written
    // one-way (debounced) so it's only ever read back when the page is re-entered.
    const [searchValue, setSearchValue] = useState(() => searchParams.get('q') ?? '')

    useEffect(() => {
        const timeout = setTimeout(() => updateParams({ q: searchValue.trim() || null }), 250)
        return () => clearTimeout(timeout)
    }, [searchValue])

    useEffect(() => {
        if (!sessionService.selectedDepartmentId) return

        departmentController.init(sessionService.selectedDepartmentId)
        departmentSchemaCategoriesController.init(sessionService.selectedDepartmentId)
        departmentDocumentSchemasController.init(sessionService.selectedDepartmentId)
    }, [sessionService.selectedDepartmentId])

    const canManage = currentUserController.canManage
    const enabledSchemas = departmentDocumentSchemasController.enabledDocumentSchemas
    const disabledSchemas = departmentDocumentSchemasController.disabledDocumentSchemas
    const uncategorizedSchemas = departmentDocumentSchemasController.uncategorizedDocumentSchemas

    const recentTemplates = documentRecentTemplatesController.getRecentSchemas(enabledSchemas, 8)

    // while searching, each category only counts (and only lists) its matching templates,
    // so the list doubles as the "which category is this result in" answer
    const searchTerm = searchValue.trim().toLowerCase()
    const isSearching = !!searchTerm
    const matchesSearch = (schema: APIDataDocumentSchema) =>
        !isSearching || !!schema.name?.toLowerCase().includes(searchTerm)

    const allCategories: BrowseCategory[] = [
        { key: 'all', label: 'All', count: enabledSchemas.filter(matchesSearch).length },
        { key: 'recents', label: 'Recents', count: recentTemplates.filter(matchesSearch).length },
        ...departmentSchemaCategoriesController.categories.map((category: APIDataDocumentSchemaCategory) => ({
            key: `category-${category.id}`,
            label: category.name as string,
            count: departmentDocumentSchemasController.schemasForCategory(category.id as number).filter(matchesSearch).length
        }))
    ]
    if (uncategorizedSchemas.length) {
        allCategories.push({ key: 'uncategorized', label: 'Uncategorized', count: uncategorizedSchemas.filter(matchesSearch).length })
    }
    if (canManage) {
        allCategories.push({ key: 'disabled', label: 'Disabled', count: disabledSchemas.filter(matchesSearch).length })
    }

    // hide categories with no matches while searching ("All" always stays as the anchor)
    const categories = isSearching
        ? allCategories.filter(c => c.key === 'all' || c.count > 0)
        : allCategories

    // no explicit selection yet: prefer Recents when the user has any, otherwise All.
    // While searching, fall back to All if the chosen category has no matches.
    const requestedKey = searchParams.get('category') ?? (recentTemplates.length ? 'recents' : 'all')
    const selectedKey = categories.some(c => c.key === requestedKey) ? requestedKey : 'all'

    const schemasForKey = (key: string): APIDataDocumentSchema[] => {
        if (key === 'all') return enabledSchemas
        if (key === 'recents') return recentTemplates
        if (key === 'uncategorized') return uncategorizedSchemas
        if (key === 'disabled') return disabledSchemas

        return departmentDocumentSchemasController.schemasForCategory(parseInt(key.replace('category-', '')))
    }

    const visibleSchemas = schemasForKey(selectedKey).filter(matchesSearch)

    const selectedLabel = categories.find(c => c.key === selectedKey)?.label || 'All'
    const panelTitle = isSearching
        ? `Search results in ${selectedLabel}`
        : selectedLabel

    const onEnableSchema = async (schema: APIDataDocumentSchema) => {
        const confirmed = await isConfirmed({
            title: 'This template is disabled',
            message: 'To create documents using this template, you must enable it. Once enabled, it will disappear from the "Disabled Templates" category and will be visible to agency members.',
            acceptButton: {
                label: 'Enable',
                color: 'primary'
            },
            cancelButton: {
                label: 'Cancel',
                color: 'link'
            }
        })

        if (!confirmed) return

        try {
            await departmentDocumentSchemasController.enableSchema(schema.id as number)

            addToast({
                color: 'success',
                message: `"${schema.name}" has been enabled.`
            })
        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Oops!',
                message: error.message || error
            })
        }
    }

    const onDisableSchema = async (schema: APIDataDocumentSchema) => {
        const confirmed = await isConfirmed({
            title: 'Disable this template?',
            message: 'Once disabled, it will move to the "Disabled Templates" category and won\'t be visible to agency members until it\'s enabled again.',
            acceptButton: {
                label: 'Disable',
                color: 'danger'
            },
            cancelButton: {
                label: 'Cancel',
                color: 'link'
            }
        })

        if (!confirmed) return

        try {
            await departmentDocumentSchemasController.disableSchema(schema.id as number)

            addToast({
                color: 'success',
                message: `"${schema.name}" has been disabled.`
            })
        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Oops!',
                message: error.message || error
            })
        }
    }

    const onCloneSchema = async (schema: APIDataDocumentSchema) => {
        if (!schema?.id || !sessionService?.selectedDepartmentId) return

        try {
            const newSchema = await cloneController.cloneSchema(schema.id, sessionService.selectedDepartmentId, schema.name + ' (copy)')

            addToast({
                color: 'success',
                title: 'Your template has been cloned',
                message: 'Edit and enable this template when ready!'
            })

            navigate(`/everdocs/schema/edit/${newSchema.id}`)

        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Clone Unsuccessful',
                message: error.message || error
            })
        }
    }

    const renderCard = (schema: APIDataDocumentSchema, itemProps: TemplateGridItemProps) => {
        if (schema.isEnabled === false) {
            return (
                <DocumentTemplateCard
                    key={schema.id}
                    schema={schema}
                    disabled
                    {...itemProps}
                    menuItems={canManage &&
                        <>
                            <MenuItem
                                icon={{ name: 'eye', className: 'me-1' }}
                                label="Enable template"
                                onClick={() => onEnableSchema(schema)}
                            />
                            <MenuItem
                                icon={{ name: 'pencil', className: 'me-1' }}
                                label="Edit template"
                                to={`/everdocs/schema/edit/${schema.id}`}
                            />
                            <MenuItem
                                icon={{ name: 'clone', className: 'me-1' }}
                                label="Clone template"
                                onClick={() => onCloneSchema(schema)}
                            />
                        </>
                    }
                />
            )
        }

        return (
            <DocumentTemplateCard
                key={schema.id}
                schema={schema}
                onClick={() => navigate(`/everdocs/add/${schema.id}`)}
                {...itemProps}
                menuItems={canManage &&
                    <>
                        <MenuItem
                            icon={{ name: 'pencil', className: 'me-1' }}
                            label="Edit template"
                            to={`/everdocs/schema/edit/${schema.id}`}
                        />
                        <MenuItem
                            icon={{ name: 'clone', className: 'me-1' }}
                            label="Clone template"
                            onClick={() => onCloneSchema(schema)}
                        />
                        <MenuItem
                            icon={{ name: 'eye-slash', className: 'me-1' }}
                            label="Disable template"
                            onClick={() => onDisableSchema(schema)}
                        />
                    </>
                }
            />
        )
    }

    const totalCount = canManage
        ? departmentDocumentSchemasController.documentSchemas.length
        : enabledSchemas.length

    return (
        <PageBody
            flush
            className="documents-page">
            <div className="px-4 pt-4">
                <Breadcrumb
                    items={[
                        { label: 'EverDocs', onClick: () => navigate('/everdocs') },
                        { label: 'Templates', active: true }
                    ]}
                />
                <Row valign="center">
                    <Text
                        tag="h3"
                        className="mb-0">
                        {departmentController.department?.name}&apos;s Templates
                    </Text>
                    <Badge
                        color="primary"
                        className="ms-2">
                        {totalCount}
                    </Badge>
                </Row>
                <Text
                    tag="p"
                    color="muted">
                    Browse categories and choose a template to create a new EverDoc.
                </Text>
            </div>
            <Row
                valign="top"
                className="px-4 pb-4">
                <Col
                    valign="top"
                    className="category-list-column pe-3">
                    <CategoryList
                        categories={categories}
                        selectedKey={selectedKey}
                        onSelect={onSelectKey}
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        panelId={templatePanelId}
                    />
                </Col>
                <Col valign="top">
                    <TemplateGrid
                        id={templatePanelId}
                        labelledBy={categoryTabId(selectedKey)}
                        title={panelTitle}
                        schemas={visibleSchemas}
                        renderCard={renderCard}
                        emptyMessage={isSearching ? `No templates match "${searchValue.trim()}".` : undefined}
                    />
                </Col>
            </Row>
        </PageBody>
    )
})

export { MyTemplates }
